import type { TableColumns, TableTagMappings } from '@ohpen/enso-ui';
import { TFunction } from 'i18next';

import { ChangeOrigin } from '@pmp/api';

import { ChangeLogEntry, ChangeLogFieldChange, ChangeLogValue } from './interfaces';

/** Value-list names the drawer builds from the entries and hands to the Table. */
export const FIELD_LIST_NAME = 'changeLogField';
export const ORIGIN_LIST_NAME = 'changeLogOrigin';

/** One row per change. The fields it touched are in the expanded detail below it. */
export const columns: TableColumns<ChangeLogEntry> = [
  {
    title: 'lender.changeLog.labels.changedAt',
    dataIndex: 'changedAt',
    key: 'changedAt',
    width: 150,
    options: {
      format: { type: 'dateTimeLocal' },
      filter: 'date',
    },
  },
  {
    title: 'lender.changeLog.labels.changedBy',
    dataIndex: 'changedBy',
    key: 'changedBy',
    width: 230,
    ellipsis: true,
    options: {
      filter: 'search',
    },
  },
  {
    title: 'lender.changeLog.labels.origin',
    dataIndex: 'origin',
    key: 'origin',
    width: 130,
    options: {
      format: { type: 'tag' },
      filter: 'select',
      listName: ORIGIN_LIST_NAME,
    },
  },
  {
    // Answers "when was the phone last changed" without opening every row: the filter keeps only the
    // changes that touched the chosen field.
    title: 'lender.changeLog.labels.fields',
    dataIndex: 'fields',
    key: 'fields',
    width: 300,
    options: {
      format: { type: 'multiValueList', ellipsis: { visibleItems: 3 } },
      filter: 'select',
      listName: FIELD_LIST_NAME,
    },
  },
  {
    title: 'lender.changeLog.labels.changeRequest',
    dataIndex: 'changeRequestId',
    key: 'changeRequestId',
    width: 135,
  },
];

/**
 * The expanded detail of one change: one row per field that moved, with what it held on either side.
 * A collection's table adds a leading column naming the row of it the field belongs to, and is
 * otherwise read exactly like the record's own, so the whole panel reads in one direction.
 */
export const getExpandedColumns = (
  emptyLabel: string,
  fieldLabel: (field: string) => string,
  valueLabel: (field: string, value: ChangeLogValue) => string | null,
  itemColumn?: { title: string; field: string },
): TableColumns<ChangeLogFieldChange> => [
  ...(itemColumn
    ? [
        {
          title: itemColumn.title,
          dataIndex: 'item',
          key: 'item',
          width: 200,
          render: (item: unknown) => valueLabel(itemColumn.field, item as ChangeLogValue),
        },
      ]
    : []),
  {
    title: 'lender.changeLog.labels.field',
    dataIndex: 'field',
    key: 'field',
    width: 220,
    render: (_: unknown, change: ChangeLogFieldChange) => fieldLabel(change.field),
  },
  {
    title: 'lender.changeLog.labels.previousValue',
    dataIndex: 'previousValue',
    key: 'previousValue',
    width: 300,
    render: (value: unknown, record: ChangeLogFieldChange) => valueLabel(record.field, value as ChangeLogValue) ?? emptyLabel,
  },
  {
    title: 'lender.changeLog.labels.newValue',
    dataIndex: 'newValue',
    key: 'newValue',
    width: 300,
    render: (value: unknown, record: ChangeLogFieldChange) => valueLabel(record.field, value as ChangeLogValue) ?? emptyLabel,
  },
];

export const getOriginTagMappings = (): TableTagMappings => ({
  origin: {
    [ChangeOrigin.PMP]: { color: '' },
    [ChangeOrigin.BMP]: { color: '' },
    [ChangeOrigin.BROKER_PORTAL]: { color: 'blue' },
    [ChangeOrigin.AUTOMATIC]: { color: 'orange' },
  },
});

/** Origin labels, keyed by the origin the backend records on each change. */
export const getOriginList = (t: TFunction): Record<string, string> => ({
  [ChangeOrigin.PMP]: t('lender.changeLog.origins.PMP'),
  [ChangeOrigin.BMP]: t('lender.changeLog.origins.BMP'),
  [ChangeOrigin.BROKER_PORTAL]: t('lender.changeLog.origins.BROKER_PORTAL'),
  [ChangeOrigin.AUTOMATIC]: t('lender.changeLog.origins.AUTOMATIC'),
});
